import '../styles/PortfolioContent.css';
import React from 'react';
import { useProjectCollection } from '../lib/projectData';

const Sweep = ({ variant, d, label }) => (
  <div className={`pc-sweep pc-sweep--${variant}`} aria-hidden="true" data-sweep="arc" data-label={label}>
    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" focusable="false">
      <path d={d} />
    </svg>
  </div>
);

const PortraitAperture = ({ photo }) => {
  const src = photo && typeof photo.photoSrc === 'string' ? photo.photoSrc.trim() : '';
  const alt = (photo && photo.photoAlt) || 'Portrait of Umang Suthar';

  return (
    <figure
      className={`pc-portrait${src ? '' : ' pc-portrait--outline'}`}
      aria-label={alt}
    >
      {src ? (
        <>
          <img className="pc-portrait__img" src={src} alt={alt} loading="eager" />
          <span className="pc-portrait__tint" aria-hidden="true" />
        </>
      ) : (
        <svg className="pc-portrait__contour" viewBox="0 0 200 200" aria-hidden="true" focusable="false">
          <path d="M14,178 C58,140 26,106 70,74 C100,52 132,44 186,22" />
          <path d="M26,186 C72,148 42,112 84,82 C112,62 144,54 192,36" />
          <path d="M40,192 C88,156 60,120 100,92 C126,74 156,66 196,52" />
        </svg>
      )}
      <span className="pc-portrait__frame" aria-hidden="true" />
      <figcaption className="pc-portrait__cap">
        <span aria-hidden="true">Portrait</span>
        <span aria-hidden="true">Fig. 01</span>
      </figcaption>
    </figure>
  );
};

export default function PortfolioContent() {
  const profiles = useProjectCollection('profiles');
  const education = useProjectCollection('education_entries');
  const languages = useProjectCollection('languages');

  const profileList = Array.isArray(profiles.data) ? profiles.data : [];
  const educationList = Array.isArray(education.data) ? education.data : [];
  const languageList = Array.isArray(languages.data) ? languages.data : [];

  const profile = profileList[0] || null;
  const name = profile && profile.name ? String(profile.name) : 'Umang Suthar';
  const displayName = (profile && profile.displayName ? String(profile.displayName) : name).trim();
  const title = profile && profile.title ? String(profile.title) : 'Full Stack Developer';

  const nameParts = displayName.split(/\s+/).filter(Boolean);
  const nameLines = nameParts.length > 1 ? [nameParts[0], nameParts.slice(1).join(' ')] : [displayName];

  const errorMessages = [
    profiles.error ? (profiles.error.message || 'Could not load the profile.') : '',
    education.error ? (education.error.message || 'Could not load education history.') : '',
    languages.error ? (languages.error.message || 'Could not load languages.') : '',
  ].filter(Boolean);

  const loading = profiles.loading || education.loading || languages.loading;
  const numeral = (index) => String(index + 1).padStart(2, '0');

  return (
    <div className="pc-root">
      {/* Band 1 — Profile stage */}
      <section className="pc-profile" aria-labelledby="pc-profile-name">
        <div className="pc-profile__pearl-sweep" aria-hidden="true" data-sweep="ellipse">
          <svg viewBox="0 0 1440 400" preserveAspectRatio="none" focusable="false">
            <path d="M0,400 L0,250 C260,70 760,10 1080,90 C1250,132 1360,220 1440,290 L1440,400 Z" />
            <path className="pc-ellipse-line" d="M0,250 C260,70 760,10 1080,90 C1250,132 1360,220 1440,290" />
          </svg>
        </div>
        <div className="pc-rail pc-profile__grid">
          <div className="pc-profile__name-block">
            <p className="pc-marker">
              <span className="pc-marker__rule" aria-hidden="true" />
              <span>Portfolio</span>
            </p>
            <h1 className="pc-profile__name" id="pc-profile-name">
              {nameLines.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </h1>
            <p className="pc-profile__title">{title}</p>
            <div className="pc-profile__meta">
              <a className="pc-link" href="#pc-education-heading">
                <span>View work</span>
                <span className="pc-link__arrow" aria-hidden="true">&rarr;</span>
              </a>
              <a className="pc-link" href="/contact">
                <span>Get in touch</span>
                <span className="pc-link__arrow" aria-hidden="true">&rarr;</span>
              </a>
            </div>
          </div>
          <PortraitAperture photo={profile} />
        </div>
      </section>

      {(errorMessages.length > 0 || loading) && (
        <div className="pc-rail" aria-live="polite">
          {errorMessages.length > 0 ? (
            <p className="pc-marker" role="alert">{errorMessages.join(' ')}</p>
          ) : (
            <p className="pc-marker">Loading profile details&hellip;</p>
          )}
        </div>
      )}

      <Sweep variant="pearl" label="profile to education" d="M0,72 Q720,8 1440,72" />

      {/* Band 2 — Education */}
      <section className="pc-education" aria-labelledby="pc-education-heading">
        <div className="pc-rail">
          <div className="pc-education__head">
            <p className="pc-marker" id="pc-education-heading">
              <span className="pc-marker__rule" aria-hidden="true" />
              <span>Education</span>
            </p>
            <p className="pc-education__intro">Academic foundations, stepped downward.</p>
          </div>

          {educationList.length > 0 ? (
            <ol className="pc-timeline">
              {educationList.map((entry, index) => (
                <li className="pc-timeline__item" key={entry.id || `${entry.degree}-${index}`}>
                  <span className="pc-timeline__node" aria-hidden="true" />
                  <p className="pc-timeline__years">{entry.years}</p>
                  <h2 className="pc-timeline__degree">{entry.degree}</h2>
                  <p className="pc-timeline__school">{entry.institution}</p>
                  <p className="pc-timeline__uni">{entry.university}</p>
                </li>
              ))}
              <li className="pc-timeline__spine" aria-hidden="true">
                <svg viewBox="0 0 1000 400" preserveAspectRatio="none" focusable="false">
                  <path d="M6,14 L70,320" />
                </svg>
              </li>
            </ol>
          ) : (
            !education.error && <p className="pc-marker">Education history is not available.</p>
          )}
        </div>
      </section>

      <Sweep variant="gold" label="education to languages" d="M0,58 Q720,74 1440,24" />

      {/* Band 3 — Languages */}
      <section className="pc-languages" aria-labelledby="pc-languages-heading">
        <div className="pc-rail">
          <p className="pc-marker pc-languages__head" id="pc-languages-heading">
            <span className="pc-marker__rule" aria-hidden="true" />
            <span>Languages</span>
          </p>
          {languageList.length > 0 ? (
            <ul className="pc-languages__list">
              {languageList.map((language, index) => (
                <li className="pc-languages__row" key={language.id || language.name}>
                  <span className="pc-languages__numeral" aria-hidden="true">{numeral(index)}</span>
                  <span className="pc-languages__name">{language.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            !languages.error && <p className="pc-marker">Languages are not available.</p>
          )}
        </div>
      </section>

      <Sweep variant="pearl" label="languages to contact" d="M0,72 Q720,8 1440,72" />

      {/* Band 4 — Contact invitation */}
      <section className="pc-contact" aria-labelledby="pc-contact-heading">
        <div className="pc-rail">
          <div className="pc-contact__panel">
            <div>
              <p className="pc-contact__wordmark" id="pc-contact-heading">
                <span className="pc-contact__rule" aria-hidden="true" />
                <span>{name}</span>
              </p>
              <p className="pc-contact__title">Open to conversations.</p>
              <p className="pc-contact__caption">Email, Twitter and GitHub</p>
            </div>
            <div className="pc-contact__action">
              <a className="pc-link" href="/contact">
                <span>Get in touch</span>
                <span className="pc-link__arrow" aria-hidden="true">&rarr;</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}